/**
 * Hook to fetch BaseTemplate / international-home site settings.
 * Used by Index, DualCTA, InvitacionExclusivaMux, BaseTemplate.
 *
 * Soft-refetch on:
 *   - admin publish (localStorage + CustomEvent SITE_SETTINGS_UPDATED)
 *   - tab visibility
 *   - poll (Mux/VIP/template changes without full reload)
 *
 * Performance (4G AR / IG WebView):
 *   - memory + sessionStorage stale-while-revalidate (avoid black screen on
 *     every mount while re-downloading ~50KB VIP map)
 *   - no cache-bust query on soft poll (browser max-age=15 from API)
 */

import { useCallback, useEffect, useState } from "react";
import { SITE_SETTINGS_UPDATED_LS_KEY } from "@/lib/siteSettingsSync";
import type { InternationalHomeVipPricing } from "@/lib/internationalHomeTemplate";

export interface SiteSettings {
  headerUrl: string;
  title: string;
  description: string;
  logoUrl?: string;
  galleryImageUrls?: string[];
  bannerImageUrl?: string;
  footerTagline?: string;
  /** Foto de perfil del header (bio-link). Vacío = HEADER_PROFILE_IMAGE_URL por defecto. */
  profileImageUrl?: string;
  /** Fondo desktop del BioLinkTemplate. Vacío = BACKGROUND_IMAGES.desktop por defecto. */
  bioBackgroundDesktopUrl?: string;
  /** Fondo mobile del BioLinkTemplate. Vacío = BACKGROUND_IMAGES.mobile por defecto. */
  bioBackgroundMobileUrl?: string;
  /** Id del formulario Tally de contacto. Vacío = TALLY_CONTACT_FORM_ID por defecto. */
  tallyContactFormId?: string;
  internationalHomeTemplate?: string;
  internationalHomeTemplateByCountry?: Record<string, string>;
  /**
   * Mux playback por país → MuxVideoPlayerWithCTA en DualCTA / InvitacionExclusivaMux.
   * Legacy: `{ "US": "playbackId" }`. Multi A/B: `{ "US": { variants: [...] } }`.
   */
  internationalHomeMuxByCountry?: Record<
    string,
    string | { variants: Array<Record<string, unknown>> }
  >;
  /** Mux playback por provincia/ciudad AR. */
  internationalHomeMuxByArGeo?: {
    provinces: Record<string, string>;
    cities: Record<string, string>;
  };
  /** Precio VIP + Stripe Price/Promo por país (DualCTA / Invitacion checkout). */
  internationalHomeVipPricingByCountry?: Record<
    string,
    {
      fullPriceUsd: number;
      firstMonthDiscountPct?: number;
      stripePriceId?: string;
      stripeProductId?: string;
      applyDiscount?: boolean;
      promotionCodeId?: string;
    }
  >;
  /**
   * Compact public API: shared VIP offer when all countries match (replaces
   * shipping 200 identical map entries on 4G).
   */
  internationalHomeVipPricingDefault?: InternationalHomeVipPricing | null;
  /** DualCTA Canal gratis global: null = heredar premium-invite-config. */
  internationalHomeDualCtaFreeTelegramEnabled?: boolean | null;
  internationalHomeDualCtaFreeTelegramByCountry?: Record<string, boolean>;
  sobreMi?: string;
  showSobreMi?: boolean;
  /** Admin toggle "Hero: ocultar todos los CTA" (site_settings.hide_global_hero_cta). */
  hideGlobalHeroCta?: boolean;
}

/** Warm defaults so AR BaseTemplate is not blank while site-settings hydrates. */
const INITIAL_SETTINGS: SiteSettings = {
  headerUrl: "/",
  title: "Ana Fernández",
  description: "",
  internationalHomeTemplate: "DualCTAVideoLanding",
};

/** Backup poll; primary refresh is SITE_SETTINGS_UPDATED broadcast + visibility. */
const POLL_MS = 30_000;
const CLIENT_CACHE_TTL_MS = 45_000;
const CACHE_PREFIX = "aaf_site_settings_cache_v2_";

type CacheBlob = { at: number; settings: SiteSettings };

const memCache = new Map<string, CacheBlob>();
/** In-flight GETs shared by prefetch + hook (avoids double download on cold 4G). */
const inflight = new Map<string, Promise<SiteSettings | null>>();

function readClientCache(variant: string): SiteSettings | null {
  const mem = memCache.get(variant);
  if (mem && Date.now() - mem.at < CLIENT_CACHE_TTL_MS) return mem.settings;
  if (typeof sessionStorage === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(CACHE_PREFIX + variant);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CacheBlob;
    if (!parsed?.settings || typeof parsed.at !== "number") return null;
    if (Date.now() - parsed.at > CLIENT_CACHE_TTL_MS * 4) return null;
    memCache.set(variant, parsed);
    return parsed.settings;
  } catch {
    return null;
  }
}

function writeClientCache(variant: string, settings: SiteSettings): void {
  const blob: CacheBlob = { at: Date.now(), settings };
  memCache.set(variant, blob);
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.setItem(CACHE_PREFIX + variant, JSON.stringify(blob));
  } catch {
    /* quota */
  }
}

/** Drop memory + session cache (admin publish / multi-tab sync). */
export function invalidateClientSiteSettingsCache(
  variant?: "live" | "test" | "all",
): void {
  const which = variant ?? "all";
  if (which === "all") {
    memCache.clear();
    inflight.clear();
    if (typeof sessionStorage !== "undefined") {
      try {
        for (const k of Object.keys(sessionStorage)) {
          if (k.startsWith(CACHE_PREFIX)) sessionStorage.removeItem(k);
        }
      } catch {
        /* ignore */
      }
    }
    return;
  }
  memCache.delete(which);
  inflight.delete(which);
  if (typeof sessionStorage !== "undefined") {
    try {
      sessionStorage.removeItem(CACHE_PREFIX + which);
    } catch {
      /* ignore */
    }
  }
}

function siteSettingsUrl(variant: string, bust: boolean): string {
  const base =
    variant === "test" ? "/api/site-settings?variant=test" : "/api/site-settings";
  if (!bust) return base;
  return `${base}${base.includes("?") ? "&" : "?"}ts=${Date.now()}`;
}

/**
 * Single-flight fetch: prefetch (main.tsx) + useSiteSettings share one network
 * round-trip on cold load (critical on AR 4G / IG WebView).
 */
function fetchSiteSettingsShared(
  variant: string,
  soft: boolean,
  signal?: AbortSignal,
): Promise<SiteSettings | null> {
  // Hard/admin bust must not reuse a soft in-flight response.
  if (!soft) {
    return fetch(siteSettingsUrl(variant, true), {
      cache: "no-store",
      signal,
      headers: {
        "Cache-Control": "no-cache",
        Pragma: "no-cache",
      },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data || typeof data !== "object") return null;
        const parsed = parseSiteSettingsPayload(data as Record<string, unknown>);
        writeClientCache(variant, parsed);
        return parsed;
      })
      .catch((error) => {
        if (error instanceof Error && error.name === "AbortError") return null;
        throw error;
      });
  }

  const existing = inflight.get(variant);
  if (existing) {
    // Abort of the consumer shouldn't cancel the shared request.
    return existing;
  }

  const p = fetch(siteSettingsUrl(variant, false), {
    cache: "default",
    // Don't pass consumer AbortSignal — other subscribers need the result.
  })
    .then((r) => (r.ok ? r.json() : null))
    .then((data) => {
      if (!data || typeof data !== "object") return null;
      const parsed = parseSiteSettingsPayload(data as Record<string, unknown>);
      writeClientCache(variant, parsed);
      return parsed;
    })
    .catch(() => null)
    .finally(() => {
      inflight.delete(variant);
    });

  inflight.set(variant, p);
  return p;
}

/**
 * Early bootstrap (main.tsx): start site-settings fetch before React mounts
 * so DualCTA/Invitacion don't wait cold on 4G AR.
 */
export function prefetchSiteSettingsEarly(variant: "live" | "test" = "live"): void {
  if (typeof window === "undefined") return;
  if (readClientCache(variant)) return;
  void fetchSiteSettingsShared(variant, true);
}

function parseSiteSettingsPayload(data: Record<string, unknown>): SiteSettings {
  return {
    headerUrl: (data.headerUrl as string) ?? "/",
    title: (data.title as string) ?? "",
    description: (data.description as string) ?? "",
    logoUrl: (data.logoUrl as string) || undefined,
    galleryImageUrls: Array.isArray(data.galleryImageUrls)
      ? (data.galleryImageUrls as string[])
      : undefined,
    bannerImageUrl:
      typeof data.bannerImageUrl === "string" && data.bannerImageUrl.trim()
        ? data.bannerImageUrl
        : undefined,
    footerTagline:
      typeof data.footerTagline === "string" && data.footerTagline.trim()
        ? data.footerTagline.trim()
        : undefined,
    profileImageUrl: (data.profileImageUrl as string) || undefined,
    bioBackgroundDesktopUrl: (data.bioBackgroundDesktopUrl as string) || undefined,
    bioBackgroundMobileUrl: (data.bioBackgroundMobileUrl as string) || undefined,
    tallyContactFormId: (data.tallyContactFormId as string) || undefined,
    internationalHomeTemplate:
      typeof data.internationalHomeTemplate === "string" &&
      data.internationalHomeTemplate.trim()
        ? data.internationalHomeTemplate.trim()
        : undefined,
    internationalHomeTemplateByCountry:
      data.internationalHomeTemplateByCountry &&
      typeof data.internationalHomeTemplateByCountry === "object" &&
      !Array.isArray(data.internationalHomeTemplateByCountry)
        ? (data.internationalHomeTemplateByCountry as Record<string, string>)
        : undefined,
    internationalHomeMuxByCountry:
      data.internationalHomeMuxByCountry &&
      typeof data.internationalHomeMuxByCountry === "object" &&
      !Array.isArray(data.internationalHomeMuxByCountry)
        ? (data.internationalHomeMuxByCountry as SiteSettings["internationalHomeMuxByCountry"])
        : undefined,
    internationalHomeMuxByArGeo: (() => {
      const raw = data.internationalHomeMuxByArGeo;
      if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
      const o = raw as { provinces?: unknown; cities?: unknown };
      const provinces =
        o.provinces && typeof o.provinces === "object" && !Array.isArray(o.provinces)
          ? (o.provinces as Record<string, string>)
          : {};
      const cities =
        o.cities && typeof o.cities === "object" && !Array.isArray(o.cities)
          ? (o.cities as Record<string, string>)
          : {};
      return { provinces, cities };
    })(),
    internationalHomeVipPricingByCountry:
      data.internationalHomeVipPricingByCountry &&
      typeof data.internationalHomeVipPricingByCountry === "object" &&
      !Array.isArray(data.internationalHomeVipPricingByCountry)
        ? (data.internationalHomeVipPricingByCountry as SiteSettings["internationalHomeVipPricingByCountry"])
        : undefined,
    internationalHomeVipPricingDefault: (() => {
      const raw = data.internationalHomeVipPricingDefault;
      if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
      const o = raw as Record<string, unknown>;
      const full =
        typeof o.fullPriceUsd === "number"
          ? o.fullPriceUsd
          : typeof o.fullPriceUsd === "string"
            ? parseFloat(o.fullPriceUsd)
            : NaN;
      if (!Number.isFinite(full) || full <= 0) return undefined;
      return raw as InternationalHomeVipPricing;
    })(),
    internationalHomeDualCtaFreeTelegramEnabled:
      data.internationalHomeDualCtaFreeTelegramEnabled === true
        ? true
        : data.internationalHomeDualCtaFreeTelegramEnabled === false
          ? false
          : null,
    internationalHomeDualCtaFreeTelegramByCountry:
      data.internationalHomeDualCtaFreeTelegramByCountry &&
      typeof data.internationalHomeDualCtaFreeTelegramByCountry === "object" &&
      !Array.isArray(data.internationalHomeDualCtaFreeTelegramByCountry)
        ? (data.internationalHomeDualCtaFreeTelegramByCountry as Record<string, boolean>)
        : undefined,
    hideGlobalHeroCta: data.hideGlobalHeroCta === true,
  };
}

export function useSiteSettings(variant: "live" | "test" = "live") {
  const cached = readClientCache(variant);
  const [settings, setSettings] = useState<SiteSettings>(
    cached || INITIAL_SETTINGS,
  );
  // If we have a warm cache, first paint is not blocked on network (AR 4G win).
  const [isLoading, setIsLoading] = useState(!cached);

  const fetchSettings = useCallback(
    async (soft: boolean, signal?: AbortSignal) => {
      try {
        if (!soft && !readClientCache(variant)) setIsLoading(true);
        const parsed = await fetchSiteSettingsShared(variant, soft, signal);
        // Apply data even if UI timeout already fired — late admin overrides still win.
        if (parsed) setSettings(parsed);
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError") return;
        if (!soft) {
          console.warn(
            "[useSiteSettings] Fetch error:",
            error instanceof Error ? error.message : error,
          );
        }
      } finally {
        setIsLoading(false);
      }
    },
    [variant],
  );

  useEffect(() => {
    const controller = new AbortController();
    // Always soft on mount so we share the main.tsx prefetch in-flight promise
    // (hard/cache-bust would start a second download on cold 4G).
    const hasCache = Boolean(readClientCache(variant));
    const timeoutId = setTimeout(() => {
      if (!hasCache) {
        controller.abort();
        console.warn("[useSiteSettings] Request timeout after 3.5s, using defaults");
        setIsLoading(false);
      }
    }, 3500);

    void fetchSettings(true, controller.signal).finally(() => {
      clearTimeout(timeoutId);
    });

    const soft = () => {
      void fetchSettings(true);
    };
    /** Admin publish: hard bust so Mux/VIP landings match live DB (not max-age=15). */
    const hardFromAdmin = () => {
      invalidateClientSiteSettingsCache(variant);
      void fetchSettings(false);
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") soft();
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key === SITE_SETTINGS_UPDATED_LS_KEY) hardFromAdmin();
    };
    const onLocalBroadcast = () => hardFromAdmin();

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("storage", onStorage);
    window.addEventListener(SITE_SETTINGS_UPDATED_LS_KEY, onLocalBroadcast);
    const poll = window.setInterval(soft, POLL_MS);

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(SITE_SETTINGS_UPDATED_LS_KEY, onLocalBroadcast);
      window.clearInterval(poll);
    };
  }, [fetchSettings, variant]);

  return { settings, isLoading };
}
